import { encryptString, decryptString } from '@/lib/secureStore';

/**
 * credentialsStore — holds the current session's licence + login credentials
 * (encrypted with AES-GCM under a systemid-derived key).
 *
 * Storage layout:
 *   - sessionStorage['chanakya.session.creds']            (always, on every login)
 *   - localStorage ['chanakya.session.creds.persistent']  (only when Remember Me is on)
 *
 * When the browser is closed and reopened, sessionStorage is wiped. If the
 * user chose "Keep me signed in", getSessionCredentials() falls back to
 * the localStorage copy and rehydrates sessionStorage for the new tab —
 * so background API calls (KPI, last-activity, etc.) keep working.
 */

const SESSION_KEY = 'chanakya.session.creds';
const PERSIST_KEY = 'chanakya.session.creds.persistent';

async function encryptCreds({ licenceid, licencepwd, username, password }) {
  const [licenceid_enc, licencepwd_enc, username_enc, password_enc] = await Promise.all([
    encryptString(licenceid),
    encryptString(licencepwd),
    encryptString(username),
    encryptString(password),
  ]);
  return { licenceid_enc, licencepwd_enc, username_enc, password_enc };
}

export async function saveSessionCredentials(creds, { persist = false } = {}) {
  const payload = JSON.stringify(await encryptCreds(creds));
  try {
    sessionStorage.setItem(SESSION_KEY, payload);
  } catch {
    /* ignore */
  }
  if (persist) {
    try {
      localStorage.setItem(PERSIST_KEY, payload);
    } catch {
      /* ignore */
    }
  } else {
    try {
      localStorage.removeItem(PERSIST_KEY);
    } catch {
      /* ignore */
    }
  }
}

export async function getSessionCredentials() {
  let raw = null;
  try {
    raw = sessionStorage.getItem(SESSION_KEY);
  } catch {
    raw = null;
  }
  if (!raw) {
    try {
      raw = localStorage.getItem(PERSIST_KEY);
    } catch {
      raw = null;
    }
    if (raw) {
      try {
        sessionStorage.setItem(SESSION_KEY, raw);
      } catch {
        /* ignore */
      }
    }
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const [licenceid, licencepwd, username, password] = await Promise.all([
    decryptString(parsed.licenceid_enc),
    decryptString(parsed.licencepwd_enc),
    decryptString(parsed.username_enc),
    decryptString(parsed.password_enc),
  ]);
  if ([licenceid, licencepwd, username, password].some((v) => v == null)) return null;
  return { licenceid, licencepwd, username, password };
}

export function clearSessionCredentials() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(PERSIST_KEY);
  } catch {
    /* ignore */
  }
}
